// PrivacyPolicy.jsx
import React from 'react';
import './PrivacyPolicy.css'; // Optional CSS import if you have a dedicated stylesheet

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <h1>Privacy Policy</h1>
      <p><strong>Effective Date:</strong> January 1st, 2025</p>

      <p>
        Welcome to <strong>privateIQ</strong>, owned and operated by <strong>Non-Target LLC</strong>
        (“we,” “us,” or “our”). We are committed to protecting your personal
        information and your right to privacy. If you have any questions or concerns
        about our policy, or our practices with regards to your personal information,
        please contact us at
        <a href="mailto:support@privateIQ.golf"> support@privateIQ.golf</a>.
      </p>

      <p>
        When you visit our website (“Site”) and use our services, you trust us with
        your personal information. We take your privacy seriously. In this Privacy
        Policy, we describe our practices regarding the collection, use, and disclosure
        of personal information. We encourage you to read through it carefully.
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        We may collect personal information that you voluntarily provide to us when
        you register on the Site, request information about our services, or otherwise
        interact with the Site. This may include:
      </p>
      <ul>
        <li>Name and contact data (email address, postal address, phone number, etc.)</li>
        <li>Login credentials (username, password)</li>
        <li>Payment information (if applicable)</li>
        <li>Information you provide in messages, forms, or user-submitted content</li>
      </ul>

      <h3>1.1 Information Automatically Collected</h3>
      <p>
        We may automatically collect certain information when you visit, use, or
        navigate our Site. This can include your IP address, browser and device
        characteristics, operating system, referring URLs, and other technical
        information. This information is primarily used to maintain the security and
        operation of our Site, and for our internal analytics and reporting purposes.
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>
        We use personal information collected via our Site for a variety of business
        purposes, such as:
      </p>
      <ul>
        <li>Facilitating account creation and logon processes</li>
        <li>Sending administrative information (e.g., technical notices, updates)</li>
        <li>Requesting feedback and contacting you about your use of the Site</li>
        <li>Protecting our Site (e.g., fraud monitoring, security checks)</li>
        <li>Other business purposes such as data analysis, identifying usage trends, and improving our services</li>
      </ul>

      <h2>3. Cookies and Other Tracking Technologies</h2>
      <h3>3.1 What Are Cookies?</h3>
      <p>
        Cookies are small data files placed on your computer or mobile device when
        you visit a website. They are widely used to make websites work more
        efficiently, as well as to provide reporting information. Cookies set by the
        website owner (in this case, privateIQ) are called “first-party cookies.”
        Cookies set by parties other than the website owner are called “third-party
        cookies.”
      </p>

      <h3>3.2 Why Do We Use Cookies?</h3>
      <p>
        We use both first-party and third-party cookies for several reasons. Some
        cookies are required for technical reasons for our Site to operate—these are
        “essential” cookies. Other cookies help us analyze usage to improve user
        experience or support marketing activities.
      </p>
      <ul>
        <li>
          <strong>Essential Cookies:</strong> Necessary to provide you with services
          and to enable the Site’s core features (e.g., session management, security).
        </li>
        <li>
          <strong>Analytics Cookies:</strong> Used to collect information about how the
          Site is used. This data helps us understand, for example, which pages are
          visited most often.
        </li>
        <li>
          <strong>Functionality Cookies:</strong> Help the Site remember user preferences
          (e.g., language settings). They provide a more personal experience.
        </li>
        <li>
          <strong>Third-Party Cookies:</strong> Placed by third-party services
          (e.g., Google Analytics, marketing platforms) for various functionalities
          or analytics.
        </li>
      </ul>

      <p>
        If you see cookies with names like `_ga`, `_gid`, `_mkto_trk`, or `ajs_user_id` in
        your browser, those may be analytics or third-party cookies for measuring
        performance, user interactions, or delivering certain features.
      </p>

      <h3>3.3 How Can You Control Cookies?</h3>
      <p>
        You have the right to decide whether to accept or reject cookies. Most
        browsers allow you to delete cookies already stored on your device and to
        prevent the placement of new cookies. However, blocking or deleting cookies
        may impact your experience on the Site.
      </p>
      <p>
        For detailed information on how to manage or delete cookies, visit your
        browser’s settings or consult online help resources for browsers like
        Chrome, Firefox, Safari, or Edge.
      </p>
      <p>
        If we use third-party analytics (e.g., Google Analytics), you can typically opt
        out directly with those providers. For example, Google offers a
        <a href="https://tools.google.com/dlpage/gaoptout"> browser add-on</a> to
        opt out of Google Analytics.
      </p>

      <h2>4. Disclosure of Your Information</h2>
      <p>
        We may share the information we have collected about you in certain situations.
        Your information may be disclosed as follows:
      </p>
      <ul>
        <li>
          <strong>By Law or to Protect Rights:</strong> We may disclose your information
          if we believe it is necessary to respond to legal processes, investigate
          potential policy violations, or protect the rights, property, or safety of
          others.
        </li>
        <li>
          <strong>Third-Party Service Providers:</strong> We may share your information
          with third parties that perform services on our behalf, such as payment
          processing, data analysis, email delivery, or hosting.
        </li>
        <li>
          <strong>Business Partners:</strong> We may share your information with our
          business partners to offer you certain products or services.
        </li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>
        We will keep your personal information only for as long as is necessary for the
        purposes set out in this Privacy Policy, unless a longer retention period is
        required or permitted by law.
      </p>

      <h2>6. Third-Party Websites</h2>
      <p>
        Our Site may contain links to third-party websites or services not owned or
        controlled by us. We are not responsible for the privacy practices of any
        third-party websites or services. We encourage you to be aware when you leave
        our Site and to review the privacy statements of each site that may collect
        your personal information.
      </p>

      <h2>7. Security of Your Information</h2>
      <p>
        We use administrative, technical, and physical security measures to help
        protect your personal information. While we take reasonable steps to secure
        your personal information, please be aware that no security measures are
        perfect or impenetrable, and no method of data transmission can be guaranteed
        against interception or misuse.
      </p>

      <h2>8. Children’s Privacy</h2>
      <p>
        We do not knowingly solicit information from or market to children under the
        age of 13. If we learn that personal information from users under 13 years of
        age has been collected, we will take reasonable measures to delete such data
        from our records.
      </p>

      <h2>9. Your Privacy Rights</h2>
      <p>
        Depending on where you reside, you may have certain rights under applicable
        data protection laws, such as the right to access, correct, or delete personal
        information we hold about you. To exercise any of these rights, please contact
        us using the contact details below.
      </p>

      <h2>10. Updates to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time in response to changing
        legal, technical, or business developments. When we update our Policy, we will
        take appropriate measures to inform you, consistent with the significance of
        the changes we make. We encourage you to periodically review this page for the
        latest information on our privacy practices.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        If you have any questions or comments about this Policy or wish to exercise
        any of your rights, you may contact us at:
      </p>
      <address>
        <strong>Non-Target LLC</strong><br />
        5511 Parkcrest Drive, Suite 103, #1000<br />
        Austin, TX 78731<br />
        United States<br />
        <a href="mailto:support@privateIQ.golf">support@privateIQ.golf</a>
      </address>

      <p>
        <strong>
          By using our Site, you acknowledge that you have read and understand
          this Privacy Policy, and you agree to be bound by its terms.
        </strong>
      </p>

    </div>
  );
}

export default PrivacyPolicy;
